import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, IdCard, UploadCloud } from "lucide-react";
import Avatar from "@/components/Avatar";
import { BiometricDevices } from "@/components/Biometric";
import { AppLockSettings } from "@/components/AppLock";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pbusy, setPbusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!user?.employee_id) return;
    const { data } = await api.get(`/employees/${user.employee_id}`);
    setProfile(data);
    setForm({ name: data.name || "", email: data.email || "", phone: data.phone || "" });
  };

  useEffect(() => { load(); }, [user]);

  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/employees/${user.employee_id}`, {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
      });
      setProfile(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/employees/${user.employee_id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      await load();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setAvatarBusy(false); }
  };

  const changePwd = async () => {
    if (!pwd.current || !pwd.next) { toast.error("Enter both passwords"); return; }
    if (pwd.next.length < 6) { toast.error("New password must be ≥ 6 characters"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("New passwords don't match"); return; }
    setPbusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password changed", { description: "You'll use the new password next time you log in" });
      setPwd({ current: "", next: "", confirm: "" });
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      toast.error("Change failed", { description: e.response?.data?.detail || e.message });
    } finally { setPbusy(false); }
  };

  const mustChange = user?.must_change_password;

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Account</div>
        <h1 className="font-heading font-black tracking-tight text-4xl mt-2">My Profile</h1>
      </header>

      {mustChange ? (
        <div className="border border-amber-300 bg-amber-50 text-amber-800 p-4" data-testid="must-change-notice">
          <div className="font-semibold text-sm">Please change your default password.</div>
          <div className="text-xs mt-1">You logged in with the seeded password. Set a new one below.</div>
        </div>
      ) : null}

      <section className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="profile-section">
        <header className="flex items-center gap-3 pb-3 border-b border-zinc-200">
          <IdCard size={18} strokeWidth={1.75} />
          <h2 className="font-heading font-bold tracking-tight text-lg">Contact details</h2>
        </header>
        {profile ? (
          <div className="flex items-start gap-4">
            <div className="relative">
              <Avatar src={profile.avatar} name={profile.name} size="xl" testid="profile-avatar" />
              {avatarBusy ? (
                <div className="absolute inset-0 bg-black/50 rounded-full grid place-items-center text-white text-[10px] font-semibold">…</div>
              ) : null}
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div className="col-span-2 text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">{profile.title}</div>
              <div className="col-span-2 flex gap-2">
                <input
                  ref={fileRef}
                  data-testid="profile-avatar-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(ev) => uploadAvatar(ev.target.files?.[0])}
                />
                <Button
                  data-testid="profile-avatar-upload-btn"
                  type="button" variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={avatarBusy}
                  className="rounded-none border-zinc-300"
                >
                  <UploadCloud size={14} className="mr-1.5" /> Change photo
                </Button>
                <span className="text-[10px] text-zinc-500 self-center">PNG / JPG / WEBP · max 5 MB</span>
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Full Name</Label>
                <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Email</Label>
                <Input data-testid="profile-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Phone</Label>
                <Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="col-span-2 flex justify-end">
                <Button data-testid="profile-save-btn" disabled={busy} onClick={saveProfile} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
                  {busy ? "Saving…" : "Save profile"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-500">Loading…</div>
        )}
      </section>

      <section className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="password-section">
        <header className="flex items-center gap-3 pb-3 border-b border-zinc-200">
          <KeyRound size={18} strokeWidth={1.75} />
          <h2 className="font-heading font-bold tracking-tight text-lg">Change password</h2>
        </header>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Current</Label>
            <Input data-testid="password-current-input" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">New</Label>
            <Input data-testid="password-new-input" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Confirm new</Label>
            <Input data-testid="password-confirm-input" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="password-save-btn" disabled={pbusy} onClick={changePwd} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            {pbusy ? "Updating…" : "Update password"}
          </Button>
        </div>
      </section>
      <BiometricDevices />
      <AppLockSettings />
    </div>
  );
}
